"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ProductsService } from "@/services/ProductsService";
import type { ProductsOutput } from "@/interfaces";
import { numberToMoney } from "@/util/moneyConversion";
import { Plus, Package } from "lucide-react";

interface SectionProductSelectorProps {
  onSelect: (product: ProductsOutput) => void;
  placeholder?: string;
  selectedProductIds?: string[];
}

export function SectionProductSelector({
  onSelect,
  placeholder = "Adicionar Produto",
  selectedProductIds = [],
}: SectionProductSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [products, setProducts] = useState<ProductsOutput[]>([]);
  const [loading, setLoading] = useState(false);

  const productsService = useMemo(() => new ProductsService(), []);

  useEffect(() => {
    // Só carregar produtos quando o popover estiver aberto
    if (!open) return;

    const loadProducts = async () => {
      setLoading(true);
      try {
        const response = await productsService.findAll({
          page: 1,
          pageSize: 100,
          query: searchValue,
          expand: ["file"],
        });
        setProducts(response.data);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [open, searchValue, productsService]);

  const handleSelectProduct = (product: ProductsOutput) => {
    onSelect(product);
    setOpen(false);
    setSearchValue("");
  };

  // Filtrar produtos já selecionados
  const availableProducts = products.filter(
    (product) => !selectedProductIds.includes(product.id)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between h-9 px-3"
          size="sm"
        >
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-blue-500" />
            <span className="text-sm">{placeholder}</span>
          </div>
          <Plus className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Buscar produtos..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? "Carregando..." : "Nenhum produto encontrado."}
            </CommandEmpty>
            <CommandGroup>
              {availableProducts.map((product) => (
                <CommandItem
                  key={product.id}
                  onSelect={() => handleSelectProduct(product)}
                  className="flex items-center justify-between p-3 cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                      {product.file ? (
                        <img
                          src={
                            typeof product.file === "string"
                              ? product.file
                              : product.file.url
                          }
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{product.name}</div>
                      <div className="text-xs text-gray-500">
                        Preço: {numberToMoney(product.unitPrice)}
                        {product.unitCost && (
                          <> | Custo: {numberToMoney(product.unitCost)}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <Plus className="h-4 w-4 text-blue-500" />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
